import { useMemo, useState, type ReactElement } from "react";
import {
  Mail,
  MonitorSmartphone,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";
import type {
  NotificationPreferenceChannel,
  NotificationPreferenceDecision,
  NotificationPreferenceEvent,
} from "@playbookai/types";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Switch,
} from "@playbookai/ui";
import {
  labelForModule,
  labelForNotificationType,
} from "../../notifications/notification-event-labels.js";

interface NotificationPreferenceListProps {
  readonly events: readonly NotificationPreferenceEvent[];
  readonly isSaving: boolean;
  readonly error: Error | null;
  readonly onDismissError: () => void;
  readonly onChange: (
    typeName: string,
    channel: NotificationPreferenceChannel,
    enabled: boolean,
  ) => void;
}

export function NotificationPreferenceList({
  events,
  isSaving,
  error,
  onDismissError,
  onChange,
}: NotificationPreferenceListProps): ReactElement {
  const [query, setQuery] = useState("");
  const [moduleId, setModuleId] = useState<string | null>(null);
  const modules = useMemo(
    () =>
      [...new Set(events.map((event) => event.moduleId))].sort((a, b) =>
        labelForModule(a).localeCompare(labelForModule(b)),
      ),
    [events],
  );
  const normalized = query.trim().toLocaleLowerCase();
  const visible = events.filter((event) => {
    if (moduleId !== null && event.moduleId !== moduleId) return false;
    if (!normalized) return true;
    const copy = labelForNotificationType(event.typeName);
    return `${copy.title} ${copy.description} ${labelForModule(event.moduleId)}`
      .toLocaleLowerCase()
      .includes(normalized);
  });
  const grouped = new Map<string | null, NotificationPreferenceEvent[]>();
  for (const event of visible) {
    const moduleEvents = grouped.get(event.moduleId) ?? [];
    moduleEvents.push(event);
    grouped.set(event.moduleId, moduleEvents);
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Kept hand-built: SectionHeader has no end-aligned live status beside
          the title block; its spacing and type differ, a look change no PR 3
          row approves. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-heading-3 tracking-tight text-pb-text">
            Alert delivery
          </h2>
          <p className="mt-0.5 text-body-small text-pb-text-muted">
            Set in-app and email delivery separately for every event.
          </p>
        </div>
        <p
          role="status"
          aria-live="polite"
          className="text-dense-1 text-pb-text-muted"
        >
          {isSaving ? "Saving your change…" : "Changes save automatically"}
        </p>
      </div>

      {error ? (
        <Alert tone="critical" title="Your change wasn't saved">
          <div className="flex items-center justify-between gap-3">
            <span>{error.message}</span>
            <Button variant="ghost" size="sm" onClick={onDismissError}>
              <X className="h-3.5 w-3.5" aria-hidden />
              Dismiss
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* Kept hand-built: Card's body padding is 16px (sm: 12/10px), not
          today's 14px, a look change no PR 3 row approves. */}
      <div className="rounded-xl border border-pb-border/80 bg-pb-card p-3.5">
        <Input
          type="search"
          aria-label="Search notification alerts"
          placeholder="Search alerts"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          prefix={<Search className="h-4 w-4" aria-hidden />}
        />
        <div
          className="mt-3 flex flex-wrap gap-1.5"
          aria-label="Filter alerts by module"
        >
          <ModuleFilterButton
            active={moduleId === null}
            label="All modules"
            onClick={() => setModuleId(null)}
          />
          {modules
            .filter((module): module is string => module !== null)
            .map((module) => (
              <ModuleFilterButton
                key={module}
                active={moduleId === module}
                label={labelForModule(module)}
                onClick={() => setModuleId(module)}
              />
            ))}
        </div>
      </div>

      {visible.length === 0 ? (
        /* The framed no-results state: EmptyState on the dashed Card
           (OK-33). */
        <Card variant="dashed">
          <EmptyState
            variant="no-results"
            title="No alerts match"
            description="Try another search or module."
          />
        </Card>
      ) : (
        [...grouped.entries()].map(([groupModuleId, moduleEvents]) => (
          /* Kept hand-built: SettingsGroup adds a 30px bottom margin, which
             this flex column (gap-5) would add to the gap between modules. */
          <section
            key={groupModuleId ?? "all"}
            aria-labelledby={`alerts-${groupModuleId ?? "all"}`}
          >
            <div className="mb-2.5 flex items-center gap-2">
              <h2
                id={`alerts-${groupModuleId ?? "all"}`}
                className="text-heading-3 tracking-tight text-pb-text"
              >
                {labelForModule(groupModuleId)}
              </h2>
              {/* Kept as today's untoned outline: tone="neutral" draws
                  the pb-border hairline instead of the muted currentColor
                  one, a look change no PR 3 row approves. */}
              <Badge variant="outline" className="text-pb-text-muted">
                {moduleEvents.length}
              </Badge>
            </div>
            {/* Kept hand-built: a responsive grid of switch rows; DataTable
                would restyle the header and the cells, a look change no PR 3
                row approves. The three-column grid follows the page's own
                width, not the window (PB-5436), from 39.5rem: the 1280px
                window's column with the rail closed, where the tracks' 38rem
                already takes 10px of the row's right padding. Below that they
                would leave the row, so it stacks instead. */}
            <div className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card">
              <div className="hidden border-b border-pb-border/60 bg-pb-card-elevated/35 px-4 py-2 text-dense-1 font-semibold text-pb-text-secondary @min-[39.5rem]/settings-page:grid @min-[39.5rem]/settings-page:grid-cols-[minmax(20rem,1fr)_8.5rem_8.5rem] @min-[39.5rem]/settings-page:gap-2">
                <span>Alert</span>
                <span className="text-center">In-app</span>
                <span className="text-center">Email</span>
              </div>
              {moduleEvents.map((event) => (
                <PreferenceRow
                  key={event.typeName}
                  event={event}
                  isSaving={isSaving}
                  onChange={onChange}
                />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function ModuleFilterButton({
  active,
  label,
  onClick,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <Button
      size="sm"
      variant={active ? "primary" : "ghost"}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

function PreferenceRow({
  event,
  isSaving,
  onChange,
}: {
  readonly event: NotificationPreferenceEvent;
  readonly isSaving: boolean;
  readonly onChange: NotificationPreferenceListProps["onChange"];
}): ReactElement {
  const copy = labelForNotificationType(event.typeName);
  const fixedReasons = event.channels.filter(
    (decision) => decision.control !== "switchable" && decision.reason,
  );
  return (
    <div className="grid gap-3 border-b border-pb-border/60 px-4 py-3.5 transition-colors hover:pb-overlay-paint last:border-b-0 @min-[39.5rem]/settings-page:grid-cols-[minmax(20rem,1fr)_8.5rem_8.5rem] @min-[39.5rem]/settings-page:items-start @min-[39.5rem]/settings-page:gap-2">
      <div className="min-w-0">
        <p className="text-body font-medium text-pb-text">{copy.title}</p>
        {copy.description ? (
          <p className="mt-0.5 text-body-small text-pb-text-muted">
            {copy.description}
          </p>
        ) : null}
      </div>
      <ChannelControl
        icon={MonitorSmartphone}
        label="In-app"
        event={event}
        channel="in_app"
        isSaving={isSaving}
        onChange={onChange}
      />
      <ChannelControl
        icon={Mail}
        label="Email"
        event={event}
        channel="email"
        isSaving={isSaving}
        onChange={onChange}
      />
      {fixedReasons.length > 0 ? (
        <div className="flex flex-col gap-1 rounded-md bg-pb-card-elevated/45 px-2.5 py-2 text-dense-2 leading-relaxed text-pb-text-muted @min-[39.5rem]/settings-page:col-span-3">
          {fixedReasons.map((decision) => (
            <p
              key={decision.channel}
              id={`${descriptionId(event.typeName, decision.channel)}-reason`}
            >
              <span className="font-semibold text-pb-text-secondary">
                {decision.channel === "in_app" ? "In-app" : "Email"}:
              </span>{" "}
              {decision.reason}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ChannelControl({
  icon: Icon,
  label,
  event,
  channel,
  isSaving,
  onChange,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly event: NotificationPreferenceEvent;
  readonly channel: NotificationPreferenceChannel;
  readonly isSaving: boolean;
  readonly onChange: NotificationPreferenceListProps["onChange"];
}): ReactElement {
  const decision = event.channels.find((item) => item.channel === channel);
  if (!decision) {
    return <span className="text-dense-1 text-pb-text-muted">Not offered</span>;
  }
  const fixed = decision.control !== "switchable";
  const status = controlStatus(decision);
  const copy = labelForNotificationType(event.typeName);
  const statusId = descriptionId(event.typeName, channel);
  return (
    <div className="min-w-0 @min-[39.5rem]/settings-page:flex @min-[39.5rem]/settings-page:flex-col @min-[39.5rem]/settings-page:items-center @min-[39.5rem]/settings-page:text-center">
      <div className="flex items-center justify-between gap-3 @min-[39.5rem]/settings-page:justify-center">
        <span className="inline-flex items-center gap-1.5 text-dense-1 font-medium text-pb-text-secondary @min-[39.5rem]/settings-page:sr-only">
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
        </span>
        <Switch
          aria-label={`${label} — ${copy.title}`}
          aria-describedby={
            decision.reason ? `${statusId} ${statusId}-reason` : statusId
          }
          checked={decision.enabled}
          disabled={fixed || isSaving}
          onChange={(enabled) => onChange(event.typeName, channel, enabled)}
        />
      </div>
      <p
        id={statusId}
        className="mt-1 text-dense-2 leading-tight text-pb-text-muted"
      >
        {status}
      </p>
    </div>
  );
}

function controlStatus(decision: NotificationPreferenceDecision): string {
  if (decision.control === "locked") return "Required";
  if (decision.control === "tenant_disabled") return "Workspace off";
  if (decision.control === "unavailable") return "Unavailable";
  return decision.userOverride === null ? "Workspace default" : "Your choice";
}

function descriptionId(
  typeName: string,
  channel: NotificationPreferenceChannel,
): string {
  return `notification-${typeName}-${channel}-status`.replaceAll(".", "-");
}
